import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
    getPastVideoSessions,
    getUpcomingVideoSessions,
    getPendingPaymentVideoSessions,
} from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { colors as dsColors } from '@modernhealth/vital-foundation';
import {
    ActionItemList,
    ActionPropsWithId,
    ComponentTextMedium,
    Modal,
    pxToRem,
} from '@modernhealth/vital-components';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { CoachingVisitNode } from 'src/generated/webapp_gql';
import { SessionDetails } from 'src/modules/videoSessions/components/SessionDetails';
import { ForYouModalInfoBar } from 'src/modules/dashboard/components/ForYouModalInfoBar';
import { getSessionTypeSlug } from 'src/modules/videoSessions/components/SessionDetailsModal';
import convertPendingPaymentSessionsToActionItems from 'src/modules/dashboard/helpers/convertPendingPaymentSessionsToActionItems';

interface ForYouModalProps {
    handleClose: VoidFunction;
    forYouActions: ActionPropsWithId[];
    isVisible: boolean;
}

enum Tabs {
    ALL = 'ALL',
    UPCOMING = 'UPCOMING',
    PAST = 'PAST',
}

export const ForYouModal: FC<ForYouModalProps> = ({ handleClose, forYouActions, isVisible }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { hpiHomescreenPendingSessions } = useFlags();

    const [activeTab, setActiveTab] = useState(Tabs.ALL);
    const [selectedSession, setSelectedSession] = useState<CoachingVisitNode | null>(null);
    const [actionItemList, setActionItemList] = useState<ActionPropsWithId[]>(forYouActions);

    const upcomingSessions = useSelector(getUpcomingVideoSessions);
    const pastSessions = useSelector(getPastVideoSessions);
    const pendingPaymentSessions: CoachingVisitNode[] = useSelector(getPendingPaymentVideoSessions);

    const getSessionType = (coachingVisit: CoachingVisitNode) => {
        const providerType = coachingVisit.coachAssignment?.coach?.providerType;
        const isPsychiatryInitialEvaluation = coachingVisit.isPsychiatryInitialEvaluation;
        return getSessionTypeSlug(providerType, isPsychiatryInitialEvaluation);
    };

    useEffect(() => {
        let pendingPaymentActionItems: ActionPropsWithId[] = [];

        if (hpiHomescreenPendingSessions) {
            pendingPaymentActionItems = convertPendingPaymentSessionsToActionItems({
                history,
                sessions: pendingPaymentSessions,
                t,
            });
        }

        setActionItemList([...forYouActions, ...pendingPaymentActionItems]);
    }, [forYouActions, history, pendingPaymentSessions, setActionItemList, t]);

    const displayForYouActions = () => (
        <ActionItemList
            title={t('dashboard:for_you.actions')}
            actions={actionItemList}
            className="actionListContainer"
            commonProps={{
                className: 'actionContainer',
            }}
        />
    );

    const displayUpcoming = () => (
        <ActionItemList
            title={t(
                `dashboard:for_you.${
                    upcomingSessions.length > 0 ? 'upcoming_sessions' : 'no_upcoming_sessions'
                }`,
            )}
            actions={upcomingSessions.map(session => ({
                id: session.uuid,
                onClick: () => setSelectedSession(session),
                title: t(`dashboard:for_you.${getSessionType(session)}_with`, {
                    name: `${session.coachAssignment?.coach?.firstName ?? ''} ${
                        session.coachAssignment?.coach?.lastName ?? ''
                    }
                `,
                }),
                description: moment(session.scheduledTime).format('dddd, MMMM D, YYYY [at] h:mm A'),
                startIconUrl: session.coachAssignment?.coach?.avatar ?? undefined,
                startIconBackgroundColor: 'blue20',
                initials: `${session.coachAssignment?.coach?.firstName?.[0] ?? ''}${
                    session.coachAssignment?.coach?.lastName?.[0] ?? ''
                }
                `,
            }))}
            className="actionListContainer"
            commonProps={{
                className: 'actionContainer',
            }}
        />
    );

    const displayPast = () => (
        <ActionItemList
            title={t(
                `dashboard:for_you.${
                    pastSessions.length > 0 ? 'past_sessions' : 'no_past_sessions'
                }`,
            )}
            actions={pastSessions.map(session => ({
                id: session.uuid,
                onClick: () => null,
                title: t(`dashboard:for_you.${getSessionType(session)}_with`, {
                    name: `${session.coachAssignment?.coach?.firstName ?? ''} ${
                        session.coachAssignment?.coach?.lastName ?? ''
                    }
                `,
                }),
                description: moment(session.scheduledTime).format('dddd, MMMM D, YYYY [at] h:mm A'),
                startIconUrl: session.coachAssignment?.coach?.avatar ?? undefined,
                startIconBackgroundColor: 'blue20',
                endIconName: 'checkmark',
                endIconColor: 'cyan40',
                startIconOpacityReduced: true,
                contentOpacityReduced: true,
                initials: `${session.coachAssignment?.coach?.firstName?.[0] ?? ''}${
                    session.coachAssignment?.coach?.lastName?.[0] ?? ''
                }
                `,
            }))}
            className="actionListContainer"
            commonProps={{
                className: 'actionContainer',
            }}
        />
    );

    const tabs = [
        {
            value: Tabs.ALL,
            label: t('dashboard:for_you.tabs.all'),
            displayContent: () => (
                <>
                    {displayForYouActions()}
                    {displayUpcoming()}
                    {displayPast()}
                </>
            ),
        },
        {
            value: Tabs.UPCOMING,
            label: t('dashboard:for_you.tabs.upcoming'),
            displayContent: displayUpcoming,
        },
        {
            value: Tabs.PAST,
            label: t('dashboard:for_you.tabs.past'),
            displayContent: displayPast,
        },
    ];

    const displayForYouContent = () => (
        <>
            <ForYouModalInfoBar />

            <TabBar>
                {tabs.map(tab => {
                    const isActive = tab.value === activeTab;
                    return (
                        <TabBarElement
                            css={[isActive && { backgroundColor: dsColors.blue55 }]}
                            onClick={() => setActiveTab(tab.value)}
                            key={tab.value}
                        >
                            <ComponentTextMedium fontColor={isActive ? 'white' : 'gray65'}>
                                {tab.label}
                            </ComponentTextMedium>
                        </TabBarElement>
                    );
                })}
            </TabBar>

            <TabBarContent>
                {tabs.find(tab => tab.value === activeTab)?.displayContent()}
            </TabBarContent>
        </>
    );

    const displaySessionDetails = () => {
        if (selectedSession) {
            return (
                <SessionDetailsContainer>
                    <SessionDetails
                        session={selectedSession}
                        onClose={() => setSelectedSession(null)}
                    />
                </SessionDetailsContainer>
            );
        }

        return null;
    };
    return (
        <ModalContainer>
            <Modal
                title={selectedSession ? undefined : t('dashboard:for_you.title')}
                description={selectedSession ? undefined : t('dashboard:for_you.description')}
                onCloseButtonClick={() => {
                    handleClose();
                    setSelectedSession(null);
                }}
                isVisible={isVisible}
            >
                {selectedSession ? displaySessionDetails() : displayForYouContent()}
            </Modal>
        </ModalContainer>
    );
};

const TabBar = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    marginTop: pxToRem(8),
});

const TabBarElement = styled.div({
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    padding: `${pxToRem(12)} 0`,
    backgroundColor: hexToRgba(dsColors.gray05, 0.5),
    cursor: 'pointer',
    userSelect: 'none',

    '&:first-of-type': {
        borderTopLeftRadius: pxToRem(4),
        borderBottomLeftRadius: pxToRem(4),
    },
    '&:last-child': {
        borderTopRightRadius: pxToRem(4),
        borderBottomRightRadius: pxToRem(4),
    },
});

const TabBarContent = styled.div({
    height: 'auto',
    width: '100%',
    maxHeight: '40vh',
    overflowY: 'auto',
    marginRight: pxToRem(-20),
    paddingRight: pxToRem(20),
    marginTop: pxToRem(32),

    '&::-webkit-scrollbar-thumb': {
        borderRadius: pxToRem(12),
        background: dsColors.gray30,
        width: pxToRem(6),
    },
    '&::-webkit-scrollbar-track': {
        background: dsColors.gray05,
        borderRadius: pxToRem(100),
        width: pxToRem(6),
    },

    '.actionListContainer + .actionListContainer': {
        marginTop: pxToRem(32),
    },

    '.actionContainer > div:first-of-type h2': {
        marginBottom: 0,
    },
});

const SessionDetailsContainer = styled.div({
    minWidth: 494,
});

const ModalContainer = styled.div({
    'div[class*="content"]': {
        borderRadius: 0,
    },
});
